import { caseActions, useAnchor, useCostOverridesByNodeAndName, useTempAnchor } from '@/stores/caseStore';
import AnchorForm from './AnchorForm';
import { anchorActions, useFindConversion, useIsSizedByNameplate } from '@/stores/anchorStore';
import { convertFlowRate } from '@/utils/unitUtils';
import { ELECTRICITY_TYPES, FLOW_RATE_MAP } from '@/consts';
import { useState } from 'react';
import CostOverridesForm from './CostOverridesForm';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { stringify } from '@/utils/miscUtils';
import Tooltip from '@/components/core/Tooltip';

const ELECTRICITY_BASIS_TOOLTIP = `The basis value represents the plant's **average yearly electricity production**, not its nameplate rating.

**Power Output** – enter the average MW the plant produces over a year.

**Rated Capacity** – enter the plant's nameplate MW; average output is calculated as rated capacity × capacity factor.

Example: a **100 MW Rated Capacity** plant at **90% capacity factor** has a **Power Output** of **90 MW**. Conversely, a **90 MW Power Output** at **90% capacity factor** implies a **Rated Capacity** of **100 MW**.`;

const NAMEPLATE_BASIS_TOOLTIP = `The basis value represents the plant's **average yearly electricity production**, not its nameplate rating.

This plant's **Plant Sizing Basis** is set to **Nameplate Capacity**, so its capacity factor is a result rather than an input and there is no **Rated Capacity** option here. Enter the average MW the plant produces over a year.`;

const AnchorFormDialog = () => {
  const { name, handle, handleType, typeName, label, id } = useTempAnchor();
  const costOverride = useCostOverridesByNodeAndName(id, name);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const anchor = useAnchor(); // from case
  const value = anchor?.io_name === name ? anchor?.quantity?.scalar : 0;
  const conversion = useFindConversion(handleType, name, anchor?.quantity?.unit);
  const isSizedByNameplate = useIsSizedByNameplate();

  const toggleForm = () => {
    setShowOverrideForm(prevState => !prevState);
  };

  const defaultValues = {
    value,
    conversion: stringify(conversion),
    measure: conversion?.unit?.measure,
  };

  const clear = () => {
    caseActions.clearTempAnchor();
    anchorActions.clear();
  };

  const onSubmit = async data => {
    const { conversion, flowRate, anchorType } = data;
    const { unit } = JSON.parse(conversion);

    let { value } = data;

    // convert back to yearly flow rate
    if (flowRate !== FLOW_RATE_MAP.year) {
      value = convertFlowRate(flowRate, 1, value);
    }

    await caseActions.patchAnchor(handle, handleType, value, unit, anchorType);

    clear();
  };

  return (
    <dialog className="modal" open>
      <div className="modal-box max-w-sm">
        <div>
          {showOverrideForm ? (
            <div className="transition-all duration-500 ease-in-out transform scale-100 relative">
              <button
                type="button"
                onClick={clear}
                className="absolute -top-4 -right-4 btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h3 className="font-bold text-lg">Override Costs: {label}</h3>
              <CostOverridesForm onToggleForm={toggleForm} onClose={clear} />
            </div>
          ) : (
            <div className="transition-all duration-500 ease-in-out transform scale-100">
              <div className="flex items-start gap-2">
                <h3 className="font-bold text-lg flex-1 min-w-0">Set Basis: {label}</h3>
                {ELECTRICITY_TYPES.includes(typeName) && (
                  <Tooltip
                    label="Electricity basis"
                    text={isSizedByNameplate ? NAMEPLATE_BASIS_TOOLTIP : ELECTRICITY_BASIS_TOOLTIP}
                  />
                )}
              </div>
              <AnchorForm
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                onCancel={clear}
                ioName={name}
                ioType={handleType}
                typeName={typeName}
                onToggleForm={toggleForm}
                costOverride={costOverride}
                showOverrideForm={handleType === 'input'}
              />
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default AnchorFormDialog;
